import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';

const navLinks = [
  { name: 'Industries', path: '/industries' },
  { name: 'How We Work', path: '/how-we-work' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const serviceLinks = [
  { name: 'All Services', path: '/services' },
  { name: 'Landscape Maintenance', path: '/landscape-maintenance' },
  { name: 'Irrigation Management', path: '/irrigation-management' },
  { name: 'Pressure Washing', path: '/pressure-washing' },
  { name: 'Exterior Asset Care', path: '/exterior-asset-care' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const isServicesActive = serviceLinks.some(link => location.pathname === link.path);

  return (
    <nav className="sticky top-0 z-50 w-full bg-white border-b border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/m2jsgssv_grounds%20care%20copy%20.png"
              alt="Sunlight Grounds Care"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className={`flex items-center gap-1 text-sm font-semibold uppercase tracking-wide transition-colors py-2 ${
                  isServicesActive
                    ? 'text-[#1a2e44] font-bold'
                    : 'text-[#1a2e44] hover:text-[#1a2e44]/70'
                }`}
                data-testid="nav-services-dropdown"
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {servicesOpen && (
                <div className="absolute top-full left-0 pt-2">
                  <div className="w-64 bg-white border border-slate-200 shadow-lg py-2">
                    {serviceLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setServicesOpen(false)}
                        className={`block px-4 py-2 text-sm transition-colors ${
                          location.pathname === link.path
                            ? 'text-[#1a2e44] font-bold bg-slate-50'
                            : 'text-slate-700 hover:bg-slate-50 hover:text-[#1a2e44]'
                        }`}
                        data-testid={`nav-service-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-semibold uppercase tracking-wide transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#1a2e44] font-bold'
                    : 'text-[#1a2e44] hover:text-[#1a2e44]/70'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-6">
            <a 
              href="tel:6267372797" 
              className="flex items-center gap-2 text-sm font-semibold text-[#1a2e44]"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              (626) 737-2797
            </a>
            <Link
              to="/contact?intent=consultation"
              className="bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-medium py-3 px-6 transition-colors"
              data-testid="nav-cta"
            >
              Schedule a Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-[#1a2e44]"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden border-t border-slate-200 py-4">
            <div className="space-y-3">
              {/* Services Section */}
              <div>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className={`flex items-center justify-between w-full text-sm font-semibold uppercase tracking-wide py-2 ${
                    isServicesActive ? 'text-[#1a2e44] font-bold' : 'text-[#1a2e44]'
                  }`}
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesOpen && (
                  <div className="pl-4 mt-2 space-y-2 border-l-2 border-slate-200">
                    {serviceLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`block text-sm py-1 ${
                          location.pathname === link.path
                            ? 'text-[#1a2e44] font-bold'
                            : 'text-slate-600'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block text-sm font-semibold uppercase tracking-wide py-2 ${
                    location.pathname === link.path
                      ? 'text-[#1a2e44] font-bold'
                      : 'text-[#1a2e44]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <a 
                href="tel:6267372797" 
                className="flex items-center gap-2 text-sm font-semibold text-[#1a2e44] py-2"
              >
                <Phone className="w-4 h-4" />
                (626) 737-2797
              </a>
              <Link
                to="/contact?intent=consultation"
                onClick={() => setIsOpen(false)}
                className="block bg-[#1a2e44] text-white text-center text-sm font-medium py-3 px-6 mt-4"
              >
                Schedule a Consultation
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
